import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import FormStepOne from './FormStepOne';
import FormStepTwo from './FormStepTwo';
import { botOperationServices } from 'features/bot-builder/services/botOperations';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import AdvancedConfiguration from '../Common/AdvancedConfiguration';
import { servicesApiService } from 'features/bot-builder/services/servicesApi';
import BotInfoFormMobile from './BotInfoFormMobile';
import useIsMobile from 'hooks/useIsMobile';


// definition for inputs
export interface botFormInputs {
  name: string;
  description: string;
  token: string;
  tele_welcome_msg: string;
  is_knowledge_base_doc_loaded: boolean;
  gpt_model_name: 'gpt-4';
  document_id?: string;
  services: number[];
}

interface BotInfoFormProps {
  setShowForm?: (value: boolean) => void;
  onBotCreated?: () => void;
  isAuthenticated?: boolean;
}

const BotInfoForm = ({
  setShowForm,
  onBotCreated,
  isAuthenticated = false,
}: BotInfoFormProps) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // to collect form inputs
  const [botInputs, setBotInputs] = useState<botFormInputs>({
    name: '',
    description: '',
    token: '',
    tele_welcome_msg: '',
    is_knowledge_base_doc_loaded: false,
    gpt_model_name: 'gpt-4',
    services: [],
  });

  const [open, setOpen] = useState(false); // State to control the dialog
  const [botId, setBotId] = useState('');
  const isMobile = useIsMobile();
  // creation function
  const botCreation = useMutation({
    mutationKey: ['BotCreation'],
    mutationFn: botOperationServices.createBot,
    onSuccess: (data) => {
      const botId = data?.data?.bot_id;
      setBotId(botId);
      setOpen(true); // opening the service limit option

      queryClient.invalidateQueries({ queryKey: ['bot-history'] });

      toast.success(`${botInputs.name} bot created successfully!`);
    },
    onError: () => {
      if (!isAuthenticated) {
        toast.error("Please login or sign up to create your new bot");
      } else {
        toast.error('Bot creation failed, Invalid form details. Please try again!');
      }
    },
  });

  const handleNewBot = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error("Please login or sign up to create your new bot");
      return;
    }

    botCreation.mutate(botInputs);
  };

  // Always fetch services to display options to all users
  const { data: allServices } = useQuery({
    queryKey: ['services'],
    queryFn: servicesApiService.getAllServices,
  });

  // Filter services by the selected IDs in botInputs.services
  const filteredServices =
    allServices?.data.filter((service: any) =>
      botInputs.services.includes(service.id),
    ) || [];

  return (
    <div className="px-3 pt-0 pb-16 max-w-[1260px] mx-auto">
      <form
        className="w-full bg-white dark:bg-[#202020] text-text-primary h-fit md:h-[550px] rounded-lg relative shadow-sm"
        onSubmit={handleNewBot}
      >
        <X
          className={`absolute ${location.pathname === '/bot-builder/get-started' && 'hidden'} right-5 top-5 cursor-pointer text-primary`}
          onClick={() => setShowForm && setShowForm(false)}
        />
        <div className="flex flex-row w-full h-full">
          {isMobile ? (
            <BotInfoFormMobile
              botInputs={botInputs}
              setBotInputs={setBotInputs}
              isDisabled={botCreation.isPending}
              pending={botCreation.isPending}
              isAuthenticated={isAuthenticated}
            />
          ) : (
            <>
              <FormStepOne botInputs={botInputs} setBotInputs={setBotInputs} />
              <FormStepTwo
                botInputs={botInputs}
                setBotInputs={setBotInputs}
                isDisabled={botCreation.isPending}
                pending={botCreation.isPending}
                isAuthenticated={isAuthenticated}
              />
            </>
          )}
        </div>
      </form>
      <AdvancedConfiguration
        open={open}
        setOpen={setOpen}
        services={filteredServices} // Pass the filtered services
        botId={botId}
        onBotCreated={onBotCreated}
      />
    </div>
  );
};

export default BotInfoForm;
